import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList
} from 'recharts';

import {
  BIAS_GRAPH_BLUE,
  BIAS_GRAPH_GREEN,
  BIAS_GRAPH_RED,
  BIAS_LLM_ALERT,
  BIAS_LLM_SOFT
} from './biasLlmChartPalette';

const CHART_COLORS = {
  primary: BIAS_GRAPH_GREEN,
  primaryLight: BIAS_GRAPH_GREEN,
  primaryDark: '#006b38',
  secondary: BIAS_GRAPH_BLUE,
  secondaryLight: '#1560b8',
  success: BIAS_GRAPH_GREEN,
  warning: '#f59e0b',
  danger: BIAS_GRAPH_RED,
  info: BIAS_GRAPH_BLUE,
  muted: '#94a3b8',
  purple: '#a855f7',
  orange: '#f97316',
  teal: '#14b8a6',
  pink: '#ec4899',
  govGateDetected: BIAS_GRAPH_BLUE,
  govGateWhitelisted: BIAS_GRAPH_GREEN,
  govGateResidual: BIAS_GRAPH_RED,
  complianceBar: BIAS_GRAPH_BLUE,
  auditBar: BIAS_GRAPH_RED
};

// --- Plot 1: Governance Gate + overlay (Total/Avg/Min/Max sensitive mentions) ---
export type GovernanceGateDataItem = {
  label: string;
  value: number;
  color: string;
};

export type GovernanceGateOverlay = {
  totalSensitiveMentions: number;
  avgPerOutput: number;
  minPerOutput: number;
  maxPerOutput: number;
  /** When API reports more all-sensitive mentions than the attribute breakdown (e.g. whitelisted). */
  allSensitiveMentions?: number | null;
  mentionScopeLabel?: string;
};

export const GovernanceGateChart = ({
  data,
  overlay,
  height = 320,
  emptyMessage = 'No governance gate data available.'
}: {
  data: GovernanceGateDataItem[] | null;
  overlay: GovernanceGateOverlay | null;
  height?: number;
  emptyMessage?: string;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  const gridStroke = '#eceff4';
  const barRadius: [number, number, number, number] = [14, 14, 0, 0];
  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 20, right: 180, left: 10, bottom: 50 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
            angle={-18}
            textAnchor="end"
            interval={0}
            axisLine={{ stroke: gridStroke }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#64748b', fontSize: 13 }}
            domain={[0, 'auto']}
            axisLine={false}
            tickLine={false}
            label={{
              value: 'Number of LLM Outputs',
              angle: -90,
              position: 'insideLeft',
              style: { fill: '#64748b', fontSize: 12 }
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              padding: '12px 16px'
            }}
            cursor={{ fill: 'rgba(100, 116, 139, 0.08)' }}
            formatter={(value: number) => [value, 'Outputs']}
            labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
          />
          <Bar dataKey="value" radius={barRadius} maxBarSize={56}>
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.color} stroke="transparent" strokeWidth={0} />
            ))}
            <LabelList dataKey="value" position="top" style={{ fontWeight: 600, fontSize: 12, fill: '#1e293b' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {overlay && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            padding: '10px 14px',
            backgroundColor: 'rgba(255,255,255,0.95)',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            fontSize: 11,
            lineHeight: 1.5,
            color: '#334155',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{overlay.mentionScopeLabel ?? 'Sensitive mentions'}</div>
          <div>Total: {overlay.totalSensitiveMentions}</div>
          {overlay.allSensitiveMentions != null && overlay.allSensitiveMentions !== overlay.totalSensitiveMentions && (
            <div style={{ color: '#64748b' }}>Incl. whitelisted: {overlay.allSensitiveMentions}</div>
          )}
          <div>Avg / output: {overlay.avgPerOutput.toFixed(1)}</div>
          <div>
            Min / Max: {overlay.minPerOutput} / {overlay.maxPerOutput}
          </div>
          <div style={{ color: '#64748b', marginTop: 4 }}>Bars = LLM outputs, not mention count</div>
        </div>
      )}
    </div>
  );
};

// --- Plot 2: Sensitive attributes (simple horizontal bars = total mentions per attribute) ---
export type SensitiveAttributeTriggerItem = {
  attribute: string;
  count: number;
};

export const SensitiveAttributesTriggeredChart = ({
  data,
  height = 340,
  emptyMessage = 'No sensitive attributes detected.'
}: {
  data: SensitiveAttributeTriggerItem[] | null;
  height?: number;
  emptyMessage?: string;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count), 0);
  const domainMax = Math.max(1, maxCount);

  return (
    <div style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart layout="vertical" data={data} margin={{ top: 8, right: 36, left: 8, bottom: 32 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, domainMax]}
            tick={{ fill: '#64748b', fontSize: 12 }}
            axisLine={{ stroke: '#e2e8f0' }}
            tickLine={false}
            allowDecimals={false}
            label={{
              value: 'Mentions (whole counts)',
              position: 'bottom',
              offset: 0,
              style: { fill: '#64748b', fontSize: 11 }
            }}
          />
          <YAxis
            type="category"
            dataKey="attribute"
            width={112}
            tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              padding: '10px 14px'
            }}
            cursor={{ fill: 'rgba(206, 58, 0, 0.06)' }}
            formatter={(value: number) => [value, 'Mentions']}
            labelFormatter={label => String(label)}
          />
          <Bar dataKey="count" fill={BIAS_GRAPH_RED} radius={[0, 8, 8, 0]} maxBarSize={28}>
            <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 600, fill: '#1e293b' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p
        style={{
          fontSize: 10,
          color: '#64748b',
          marginTop: 6,
          marginBottom: 0,
          paddingLeft: 4
        }}
      >
        Totals combine prompt, metadata, and response text. Not the number of LLM runs.
      </p>
    </div>
  );
};

const SENSITIVE_ATTR_LEDE_DEFAULT = 'How often each attribute showed up — prompt, metadata, and output combined.';

function SensitiveAttributesFrequencyHeader({
  title,
  lede,
  total,
  totalLabel,
  softPalette,
  barsAllBlue
}: {
  title?: string;
  lede: string;
  total: number;
  totalLabel?: string;
  /** Soft colors for developer; default teal accent for others. */
  softPalette?: boolean;
  /** Developer role: all bar accents use graph blue only. */
  barsAllBlue?: boolean;
}) {
  const totalColor = barsAllBlue ? BIAS_GRAPH_BLUE : softPalette ? BIAS_LLM_SOFT.governanceResidual : BIAS_GRAPH_GREEN;
  return (
    <div className="d-flex flex-column flex-sm-row flex-sm-nowrap align-items-start align-items-sm-end justify-content-between gap-2 gap-sm-3 mb-3 pb-2 border-bottom border-secondary-subtle">
      <div className="min-w-0 flex-grow-1">
        {title ? (
          <div className="fw-semibold text-body mb-1" style={{ fontSize: '1rem', lineHeight: 1.3 }}>
            {title}
          </div>
        ) : null}
        <p className="text-muted mb-0" style={{ fontSize: 12, lineHeight: 1.4 }}>
          {lede}
        </p>
      </div>
      <div className="text-end flex-shrink-0 align-self-end ms-sm-2">
        <div className="text-muted text-uppercase" style={{ fontSize: 10, letterSpacing: '0.06em' }}>
          {totalLabel ?? 'Total mentions'}
        </div>
        <div className="fs-4 fw-bold text-body lh-sm tabular-nums" style={{ color: totalColor }}>
          {total}
        </div>
      </div>
    </div>
  );
}

/** Ranked list + share bars — alternative UI for combined sensitive-attribute counts. */
export const SensitiveAttributesFrequencyPanel = ({
  data,
  maxHeight = 320,
  emptyMessage = 'No sensitive attributes detected.',
  title,
  lede = SENSITIVE_ATTR_LEDE_DEFAULT,
  totalLabel = 'Total mentions',
  totalMentions,
  softPalette = false,
  barsAllBlue = false
}: {
  data: SensitiveAttributeTriggerItem[] | null;
  maxHeight?: number;
  emptyMessage?: string;
  /** When set, shown as the main heading in the panel header (keeps title + stats on one aligned row). */
  title?: string;
  lede?: string;
  totalLabel?: string;
  /** When set, used for the header total instead of summing `data` (keeps panel aligned with chart overlay). */
  totalMentions?: number;
  softPalette?: boolean;
  /** Developer role: share bars and header accent use graph blue only. */
  barsAllBlue?: boolean;
}) => {
  const total = totalMentions ?? data?.reduce((sum, d) => sum + d.count, 0) ?? 0;
  const barFill = barsAllBlue ? BIAS_GRAPH_BLUE : softPalette ? BIAS_LLM_SOFT.governanceResidual : BIAS_GRAPH_GREEN;
  const rowBorder = barsAllBlue || softPalette ? 'rgba(9, 63, 136, 0.15)' : 'rgba(0, 140, 70, 0.12)';
  const trackBg = barsAllBlue || softPalette ? 'rgba(9, 63, 136, 0.1)' : 'rgba(0, 140, 70, 0.12)';

  if (!data || data.length === 0) {
    return (
      <div style={{ maxHeight, overflowY: 'auto' }} className="pe-sm-1">
        <SensitiveAttributesFrequencyHeader
          title={title}
          lede={lede}
          total={total}
          totalLabel={totalLabel}
          softPalette={softPalette}
          barsAllBlue={barsAllBlue}
        />
        <div
          className="d-flex align-items-center justify-content-center text-muted rounded-3 border border-dashed border-secondary-subtle"
          style={{ minHeight: 120 }}
        >
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxHeight, overflowY: 'auto' }} className="pe-sm-1">
      <SensitiveAttributesFrequencyHeader
        title={title}
        lede={lede}
        total={total}
        totalLabel={totalLabel}
        softPalette={softPalette}
        barsAllBlue={barsAllBlue}
      />

      <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
        {data.map((row, index) => {
          const sharePct = total > 0 ? (row.count / total) * 100 : 0;
          const barWidthPct = total > 0 ? (row.count / total) * 100 : 0;
          return (
            <li
              key={row.attribute}
              className="rounded-3 border border-secondary-subtle bg-body-secondary bg-opacity-25 px-3 py-2"
              style={{ borderColor: rowBorder }}
            >
              <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                <div className="d-flex align-items-center gap-2 min-w-0 flex-grow-1">
                  <span
                    className="flex-shrink-0 rounded-pill bg-white border text-muted fw-semibold d-inline-flex align-items-center justify-content-center"
                    style={{ width: 22, height: 22, fontSize: 11 }}
                  >
                    {index + 1}
                  </span>
                  <span className="fw-semibold text-body text-break" style={{ fontSize: 13 }}>
                    {row.attribute}
                  </span>
                </div>
                <div className="text-end flex-shrink-0">
                  <span className="fw-bold text-body" style={{ fontSize: 14 }}>
                    {row.count}
                  </span>
                  <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                    ({sharePct < 10 ? sharePct.toFixed(1) : Math.round(sharePct)}%)
                  </span>
                </div>
              </div>
              <div
                className="rounded-pill overflow-hidden"
                style={{ height: 6, background: trackBg }}
                title={`${sharePct.toFixed(1)}% of all mentions`}
              >
                <div
                  className="h-100 rounded-pill"
                  style={{
                    width: `${barWidthPct}%`,
                    background: barFill,
                    minWidth: row.count > 0 ? 4 : 0,
                    transition: 'width 0.35s ease'
                  }}
                />
              </div>
              <div className="mt-1">
                <span className="text-muted" style={{ fontSize: 10 }}>
                  Portion of all mentions
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <p className="text-muted mb-0 mt-3" style={{ fontSize: 10, lineHeight: 1.45 }}>
        Counts are summed detections across prompt, metadata, and response text — not the number of LLM runs.
      </p>
    </div>
  );
};

export const MLBiasBarChart = ({
  data,
  height = 360,
  emptyMessage = 'No bias metrics available for visualization.'
}: {
  data: { attribute: string; disparityRatio: number }[] | null;
  height?: number;
  emptyMessage?: string;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="attribute"
          tick={{ fill: '#64748b', fontSize: 13, fontWeight: 500 }}
          angle={data.length > 5 ? -20 : 0}
          textAnchor={data.length > 5 ? 'end' : 'middle'}
          interval={0}
          axisLine={{ stroke: '#e2e8f0' }}
          tickLine={false}
        />
        <YAxis tick={{ fill: '#64748b', fontSize: 13 }} domain={[0, 'auto']} axisLine={false} tickLine={false} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            padding: '12px 16px'
          }}
          cursor={{ fill: 'rgba(0, 140, 70, 0.1)' }}
          formatter={(value: number) => [value.toFixed(3), 'Disparity Ratio']}
          labelFormatter={label => `Attribute: ${label}`}
          labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
        />
        <Bar dataKey="disparityRatio" fill={CHART_COLORS.primary} radius={[8, 8, 0, 0]} maxBarSize={60} />
      </BarChart>
    </ResponsiveContainer>
  );
};

type FunnelDataItem = {
  label: string;
  count: number;
  percentage: number;
  color: string;
};

const funnelTooltipFormatter = (
  value: number,
  _name: string,
  entry: { payload?: FunnelDataItem }
): [string, string] => [`${value} (${entry.payload?.percentage ?? 0}%)`, 'Datasets'];

export const FunnelBarChart = ({
  data,
  height = 360,
  emptyMessage = 'No funnel data available for bias validation.'
}: {
  data: FunnelDataItem[] | null;
  height?: number;
  emptyMessage?: string;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }}
          angle={-15}
          textAnchor="end"
          interval={0}
          axisLine={{ stroke: '#e2e8f0' }}
          tickLine={false}
        />
        <YAxis tick={{ fill: '#64748b', fontSize: 13 }} domain={[0, 'auto']} axisLine={false} tickLine={false} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            padding: '12px 16px'
          }}
          cursor={{ fill: 'rgba(9, 63, 136, 0.1)' }}
          formatter={funnelTooltipFormatter}
          labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
        />
        <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={60}>
          {data.map((entry, index) => (
            <Cell key={index} fill={entry.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const VerdictPieChart = ({
  data,
  height = 320,
  emptyMessage = 'No verdict distribution data available.'
}: {
  data: { name: string; value: number; color: string }[] | null;
  height?: number;
  emptyMessage?: string;
}) => {
  if (!data || data.every(d => d.value === 0)) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  const filteredData = data.filter(d => d.value > 0);
  if (filteredData.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={filteredData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
        >
          {filteredData.map((entry, index) => (
            <Cell key={index} fill={entry.color} stroke="#fff" strokeWidth={2} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
          formatter={(value: number) => [`${value} entries`, 'Count']}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

export const VerdictBarChart = ({
  data,
  height = 320,
  showRejectNote = true,
  emptyMessage = 'No verdict distribution data available.'
}: {
  data: { name: string; value: number; color: string }[] | null;
  height?: number;
  showRejectNote?: boolean;
  emptyMessage?: string;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  const gridStroke = '#eceff4';
  const barRadius: [number, number, number, number] = [14, 14, 0, 0];
  return (
    <div style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 20,
            left: 10,
            bottom: showRejectNote ? 44 : 10
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fill: '#64748b', fontSize: 13, fontWeight: 500 }}
            axisLine={{ stroke: gridStroke }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#64748b', fontSize: 13 }}
            domain={[0, 'auto']}
            axisLine={false}
            tickLine={false}
            label={{
              value: 'Number of Entries',
              angle: -90,
              position: 'insideLeft',
              style: { fill: '#64748b', fontSize: 11 }
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              padding: '12px 16px'
            }}
            cursor={{ fill: 'rgba(100, 116, 139, 0.07)' }}
            formatter={(value: number) => [`${value}`, 'Entries']}
            labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
          />
          <Bar dataKey="value" radius={barRadius} maxBarSize={80}>
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.color} stroke="transparent" strokeWidth={0} />
            ))}
            <LabelList dataKey="value" position="top" style={{ fontWeight: 600, fontSize: 12, fill: '#1e293b' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {showRejectNote && (
        <p
          style={{
            fontSize: 10,
            color: '#64748b',
            marginTop: 4,
            marginBottom: 0,
            paddingLeft: 4
          }}
        >
          REJECT occurs when non-whitelisted sensitive attributes exceed risk thresholds (Stereotype Score / Association
          Skew / Group Favoritism / Hostility Score).
        </p>
      )}
    </div>
  );
};

export const ComplianceVsAuditChart = ({
  data,
  microeaiBiasScore,
  height = 360,
  emptyMessage = 'No compliance vs audit metrics available.',
  softPalette = false,
  barsAllBlue = false
}: {
  data: { metric: string; compliance: number; audit: number | null }[] | null;
  microeaiBiasScore?: number | null;
  height?: number;
  emptyMessage?: string;
  softPalette?: boolean;
  /** Developer role: both series use graph blue only. */
  barsAllBlue?: boolean;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  const gridStroke = '#eceff4';
  const compFill = barsAllBlue ? BIAS_GRAPH_BLUE : CHART_COLORS.complianceBar;
  const auditFill = barsAllBlue
    ? BIAS_GRAPH_BLUE
    : softPalette
    ? BIAS_LLM_SOFT.complianceAuditBar
    : BIAS_LLM_ALERT.complianceAuditBar;
  const barRadius: [number, number, number, number] = [12, 12, 0, 0];
  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis
            dataKey="metric"
            tick={{ fill: '#64748b', fontSize: 13, fontWeight: 500 }}
            axisLine={{ stroke: gridStroke }}
            tickLine={false}
          />
          <YAxis tick={{ fill: '#64748b', fontSize: 13 }} domain={[0, 1.05]} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              padding: '12px 16px'
            }}
            cursor={{ fill: 'rgba(9, 63, 136, 0.08)' }}
            labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
          />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px',
              fontWeight: 500
            }}
            iconType="circle"
          />
          <Bar dataKey="compliance" name="Compliance (pre-review)" fill={compFill} radius={barRadius} maxBarSize={50}>
            <LabelList
              dataKey="compliance"
              position="top"
              style={{ fontSize: 10, fontWeight: 600 }}
              formatter={(v: number) => v.toFixed(3)}
            />
          </Bar>
          <Bar dataKey="audit" name="Audit (post-review)" fill={auditFill} radius={barRadius} maxBarSize={50}>
            <LabelList
              dataKey="audit"
              position="top"
              style={{ fontSize: 10, fontWeight: 600 }}
              formatter={(v: number) => (v != null ? v.toFixed(3) : '')}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {microeaiBiasScore != null && !Number.isNaN(microeaiBiasScore) && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: '8px 12px',
            backgroundColor: 'rgba(255,255,255,0.95)',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: '#334155',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          MicroEAI Bias Score: {Number(microeaiBiasScore).toFixed(3)}
        </div>
      )}
    </div>
  );
};

// ML Bias: Disparity Ratio + Calculated Impact (grouped bar, risk-based colors for disparity)
export type MLBiasExtendedItem = {
  attribute: string;
  disparityRatio: number;
  calculatedImpact: number;
  riskColor: string;
};

export const MLBiasGroupedBarChart = ({
  data,
  height = 360,
  emptyMessage = 'No bias metrics available for visualization.'
}: {
  data: MLBiasExtendedItem[] | null;
  height?: number;
  emptyMessage?: string;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="attribute"
          tick={{ fill: '#64748b', fontSize: 13, fontWeight: 500 }}
          angle={data.length > 5 ? -20 : 0}
          textAnchor={data.length > 5 ? 'end' : 'middle'}
          interval={0}
          axisLine={{ stroke: '#e2e8f0' }}
          tickLine={false}
        />
        <YAxis tick={{ fill: '#64748b', fontSize: 13 }} domain={[0, 'auto']} axisLine={false} tickLine={false} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            padding: '12px 16px'
          }}
          cursor={{ fill: 'rgba(0, 140, 70, 0.1)' }}
          labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
        />
        <Legend
          wrapperStyle={{
            paddingTop: '12px',
            fontSize: '14px',
            fontWeight: 500
          }}
          iconType="circle"
        />
        <Bar dataKey="disparityRatio" name="Disparity Ratio" radius={[8, 8, 0, 0]} maxBarSize={40}>
          {data.map((entry, index) => (
            <Cell key={index} fill={entry.riskColor} />
          ))}
        </Bar>
        <Bar
          dataKey="calculatedImpact"
          name="Calculated Impact"
          fill={CHART_COLORS.info}
          radius={[8, 8, 0, 0]}
          maxBarSize={40}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Risk distribution pie (High / Medium / Low)
export type RiskDistributionItem = {
  name: string;
  value: number;
  color: string;
};

export const RiskDistributionPieChart = ({
  data,
  height = 280,
  emptyMessage = 'No risk distribution data available.'
}: {
  data: RiskDistributionItem[] | null;
  height?: number;
  emptyMessage?: string;
}) => {
  if (!data || data.every(d => d.value === 0)) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  const filteredData = data.filter(d => d.value > 0);
  if (filteredData.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={filteredData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={90}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
        >
          {filteredData.map((entry, index) => (
            <Cell key={index} fill={entry.color} stroke="#fff" strokeWidth={2} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            padding: '12px 16px'
          }}
          formatter={(value: number) => [`${value} attributes`, 'Count']}
        />
        <Legend
          wrapperStyle={{
            paddingTop: '8px',
            fontSize: '14px',
            fontWeight: 500
          }}
          iconType="circle"
        />
      </PieChart>
    </ResponsiveContainer>
  );
};
